import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { onboardingAPI } from '../../services/api';
import { REASON_TEXT } from './onboardingTypes';

interface PromptData {
  show_banner: boolean;
  auto_open: boolean;
  reason: 'new' | 'upgrade' | 'incomplete' | null;
  pending_assets: { id: number; name: string; percent: number; required_percent: number; reason: string }[];
  total_assets: number;
}

// Non-blocking "finish your setup" prompt for the home dashboard.
// - first visit after a new onboarding version: opens /onboarding once
// - otherwise a dismissible banner ("remind me in 3 days")
const OnboardingBanner: React.FC = () => {
  const { currentOrganization } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<PromptData | null>(null);
  const [busy, setBusy] = useState(false);
  const orgId = currentOrganization?.id;

  useEffect(() => {
    let alive = true;
    onboardingAPI
      .prompt(orgId)
      .then(async (r) => {
        const data: PromptData = r.data?.data;
        if (!alive || !data) return;
        // Auto-open the overview once per release (also guarded per tab session).
        const sessionKey = `onboarding_auto_opened_${orgId || 'default'}`;
        let alreadyThisSession = false;
        try { alreadyThisSession = sessionStorage.getItem(sessionKey) === '1'; } catch { /* ignore */ }
        if (data.auto_open && !alreadyThisSession) {
          try { sessionStorage.setItem(sessionKey, '1'); } catch { /* ignore */ }
          await onboardingAPI.markOpened(orgId).catch(() => undefined);
          if (alive) navigate('/onboarding?welcome=1');
          return;
        }
        setPrompt(data);
      })
      .catch(() => undefined); // never block the dashboard
    return () => { alive = false; };
  }, [orgId, navigate]);

  if (!prompt || !prompt.show_banner || prompt.pending_assets.length === 0) return null;

  const pending = prompt.pending_assets;
  const single = pending.length === 1 ? pending[0] : null;
  const avg = Math.round(pending.reduce((s, p) => s + p.percent, 0) / pending.length);

  const dismiss = async () => {
    setBusy(true);
    try {
      await onboardingAPI.dismissPrompt(orgId);
      setPrompt({ ...prompt, show_banner: false });
    } catch {
      setPrompt({ ...prompt, show_banner: false });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        background: 'linear-gradient(90deg,#eef2ff 0%,#f5f3ff 100%)', border: '1px solid #c7d2fe',
        borderRadius: 10, padding: '10px 14px', marginBottom: 12,
      }}
    >
      <div style={{ width: 36, height: 36, borderRadius: 8, background: '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className="bi bi-list-check"></i>
      </div>
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e1b4b' }}>
          {REASON_TEXT[prompt.reason || 'incomplete'] || REASON_TEXT.incomplete}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#4338ca' }}>
          {single
            ? <>{single.name} is {single.percent}% set up.</>
            : <>{pending.length} of {prompt.total_assets} assets need a quick check · {avg}% average.</>}
          {' '}We verify each step automatically.
        </div>
        <div style={{ height: 4, background: '#e0e7ff', borderRadius: 2, marginTop: 6, maxWidth: 320 }}>
          <div style={{ height: 4, width: `${Math.max(4, avg)}%`, background: '#4f46e5', borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-sm btn-primary" onClick={() => navigate(single ? `/onboarding/${single.id}` : '/onboarding')}>
          Continue setup <i className="bi bi-arrow-right ms-1"></i>
        </button>
        <button className="btn btn-sm btn-link text-muted" disabled={busy} onClick={dismiss} title="Hide this for 3 days">
          Remind me later
        </button>
      </div>
    </div>
  );
};

export default OnboardingBanner;
